<script setup>
import { WIcon, WLabel } from '@alice-health/wonderland-vue'
import { vMaska } from 'maska'
import {
  computed,
  getCurrentInstance,
  nextTick,
  onMounted,
  ref,
  watch
} from 'vue'

const props = defineProps({
  name: {
    type: String,
    default: undefined
  },
  label: {
    type: String,
    default: undefined
  },
  type: {
    type: String,
    default: 'text'
  },
  placeholder: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  maxLength: {
    type: Number,
    default: undefined
  },
  minLength: {
    type: Number,
    default: undefined
  },
  autoFocus: {
    type: Boolean,
    default: false
  },
  value: {
    type: [String, Number],
    default: undefined
  },
  icon: {
    type: String,
    default: undefined
  },
  maskPattern: {
    type: [String, Array],
    default: undefined
  },
  autocomplete: {
    type: String,
    default: 'off'
  }
})

const emit = defineEmits([
  'update:value',
  'focus',
  'blur',
  'key',
  'keydown',
  'click',
  'keyup'
])

const elementId = props.name || getCurrentInstance().uid

const input = ref(null)

const mask = computed(() => {
  if (!props.maskPattern) return null
  if (Array.isArray(props.maskPattern))
    return props.maskPattern ? `["${props.maskPattern.join('", "')}"]` : null
  return props.maskPattern
})

const setFocus = () => {
  nextTick(() => {
    input.value.focus()
  })
}

onMounted(() => {
  if (props.autoFocus) {
    setFocus()
  }
})

watch(
  () => props.autoFocus,
  value => {
    if (value) {
      setFocus()
    }
  }
)

watch(
  () => mask.value,
  () => {
    nextTick(() => {
      input.value.focus()
    })
  }
)

const onInput = event => {
  emit('update:value', event.target.value)
}

const onInputMask = event => {
  emit('update:value', event.detail?.masked)
}

const onFocus = event => {
  emit('focus', event)
}

const onBlur = event => {
  emit('blur', event)
}

const onKeyPress = event => {
  emit('key', event)
}

const onKeyDown = event => {
  emit('keydown', event)
}

const onKeyUp = event => {
  emit('keyup', event)
}

const onClick = event => {
  emit('click', event)
}

defineExpose({
  focus: () => {
    input.value.focus()
  }
})
</script>

<template>
  <div class="h-textfield" :class="{ 'h-textfield--icon': icon }">
    <WLabel v-if="label" :for="elementId">{{ label }}</WLabel>
    <div class="h-textfield__wrapper">
      <WIcon v-if="icon" :icon="icon" class="h-textfield__icon" />
      <input
        v-if="mask"
        v-bind="$attrs"
        :id="elementId"
        ref="input"
        v-maska
        :data-maska="mask"
        :name="elementId"
        class="h-textfield__input"
        :type="type"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :readonly="readonly"
        :maxlength="maxLength"
        :minlength="minLength"
        :autofocus="autoFocus"
        :autocomplete="autocomplete"
        :value="value"
        @focus="onFocus"
        @blur="onBlur"
        @key="onKeyPress"
        @keydown="onKeyDown"
        @keyup="onKeyUp"
        @click="onClick"
        @maska="onInputMask"
      />
      <input
        v-else
        v-bind="$attrs"
        :id="elementId"
        ref="input"
        :name="elementId"
        class="h-textfield__input"
        :type="type"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :readonly="readonly"
        :maxlength="maxLength"
        :minlength="minLength"
        :autofocus="autoFocus"
        :autocomplete="autocomplete"
        :value="value"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @key="onKeyPress"
        @keydown="onKeyDown"
        @keyup="onKeyUp"
        @click="onClick"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.h-textfield {
  width: 100%;

  &__wrapper {
    position: relative;
  }

  &__input {
    background-color: var(--gl-color-shades-white);
    border: var(--gl-border-width-xs) solid var(--gl-color-shades-gray-20);
    color: var(--gl-color-shades-gray-100);
    cursor: text;
    border-radius: var(--gl-border-radius-sm);
    display: block;
    caret-color: var(--gl-color-shades-magenta-50);
    line-height: var(--gl-line-height-xxs);
    font-size: var(--gl-font-size-xxs);
    font-weight: var(--gl-font-weight-light);
    width: 100%;
    padding: var(--gl-spacing-02) var(--gl-spacing-03);
    resize: none;
    font-family: var(--gl-font-family-base);
    height: 42px;

    &:focus {
      outline: none;
      border-color: var(--gl-color-shades-magenta-50);
    }

    &:read-only {
      background-color: var(--gl-color-shades-gray-10);
    }
  }

  &--icon {
    .h-textfield__input {
      padding-left: var(--gl-spacing-08);
    }
  }

  &__icon {
    position: absolute;
    display: flex;
    align-items: center;
    padding-left: var(--gl-spacing-02);
    height: 100%;
  }
}
</style>
